import { KeyValuePair } from "utilities/objects";

export type CampaignConditionType =
  | "achievementDropdown"
  | "array"
  | "boolean"
  | "countryDropdown"
  | "date"
  | "datetime"
  | "expression"
  | "float"
  | "genderDropdown"
  | "int"
  | "itemsCriteria"
  | "label"
  | "number"
  | "segmentDropdown"
  | "text"
  | "tierDropdown"
  | "tierDropdownSingle"
  | "channelDropdown"
  | "transactionType";

export enum OperatorId {
  EQUAL = "eq",
  NOT_EQUAL = "neq",
  STARTS_WITH = "starts_with",
  ENDS_WITH = "ends_with",
  MATCH = "match",
  NOT_MATCH = "not_match",
  IN = "in",
  NOT_IN = "not_in",
  IN_VALUE_GROUP = "in_value_group",
  NOT_IN_VALUE_GROUP = "not_in_value_group",
  GREATER = "gt",
  GREATER_OR_EQUAL = "gte",
  LESS = "lt",
  LESS_OR_EQUAL = "lte",
  BETWEEN = "between",
  NOT_BETWEEN = "not_between",
  ONE_OF = "one_of",
  NOT_ONE_OF = "not_one_of",
  ONE_OF_VALUE_GROUP = "one_of_value_group",
  NOT_ONE_OF_VALUE_GROUP = "not_one_of_value_group",
}

export enum NewFormatConditionType {
  STRING = "string",
  NUMBER = "number",
  LABELS_ARRAY = "labelsArray",
}

export type NewFormatConditionValue =
  | string
  | string[]
  | number
  | number[]
  | { groupValuesIds: string[] }
  | { from: number; to: number }
  | { key: string; value: string }[]
  | { labelKey: string; groupValuesIds: string[] };

export type CampaignCondition = {
  attribute: string;
  label: string;
  type?: CampaignConditionType;
  data?: null | [] | boolean;
  operator?: string;
  isDeprecated?: boolean;
  labelInfo?: string | React.ReactNode;
};

export type CampaignConditionNewFormatParam = {
  walletTypeCode?: string;
  badgeTypeId?: string;
  field?: string;
};

export type CampaignConditionNewFormat = {
  label: string;
  value: {
    field: string;
    aggregation?: {
      type: string;
      field: string;
    };
    filterCode?: string;
    params?: CampaignConditionNewFormatParam;
  };
  condition?: {
    operator: `${OperatorId}`;
    value: NewFormatConditionValue;
  };
  type: NewFormatConditionType;
  labelInfo?: string | React.ReactNode;
};

export type CampaignConditionData =
  | { from: string; to: string }
  | { key: string; value: string }[]
  | boolean
  | number
  | string
  | string[]
  | { groupOfValues: string[] | string; attributeKey?: string };

export type CampaignEffectType =
  | "assign_member_custom_attribute"
  | "deduct_unit"
  | "give_points"
  | "give_reward"
  | "remove_member_custom_attribute";

export type Condition = {
  attribute: string;
  data: CampaignConditionData;
  description?: string;
  label?: string;
  name?: string;
  operator: string;
  param?: string | ItemCriteriaParam[];
  type?: CampaignConditionType;
};

export type ConditionType = Condition & {
  isEditing?: boolean;
  isSaved?: boolean;
  draft?: Condition;
};

export type ItemCriteriaParam = {
  criterion: string;
  values: string[] | KeyValuePair[];
};

export type UnitsExpirationRule = {
  expirationStrategy: "expression" | "never_expires";
  expression?: string;
};

export type UnitsLockRule = {
  lockStrategy: "expression" | "no_pending";
  expression?: string;
};

export type Effect = {
  couponValueRule?: string;
  customAttributeKey?: string;
  customAttributeValueRule?: string;
  effect?: CampaignEffectType;
  label?: string;
  pointsRule?: string;
  rewardId?: string;
  rule?: string;
  walletCode?: string;
  unitsExpirationRule?: UnitsExpirationRule;
  unitsLockRule?: UnitsLockRule;
  isChecked?: boolean;
};

export type CampaignEffect = Effect & {
  isEditing?: boolean;
  isSaved?: boolean;
  draft?: Effect;
};

export type DirectCampaignRule = {
  conditions: (
    | ConditionType
    | (CampaignConditionNewFormat & {
        isEditing?: boolean;
        isSaved?: boolean;
        draft?: CampaignConditionNewFormat;
      })
  )[];
  effects: CampaignEffect[];
  name?: string;
  description?: string;
};

/**
 * Available campaign targets - "self" is the equivalent of referee or customer
 */

export enum CampaignRuleTargetType {
  SELF = "self",
  REFERRER = "referrer",
}

export type CampaignRuleTarget = `${CampaignRuleTargetType}`;

export type ReferralCampaignRule = DirectCampaignRule & {
  target: CampaignRuleTarget;
};
